import { createSelector } from "reselect"
import { get, chain, find, pickBy, filter } from "lodash"
import moment from "moment"
import { getAllItems, getOrderedCategoryItems } from "./items"
import { getFilteredRisk, getRiskAttributionData } from "./risk"
import { COLORS, ANALYZE_FACTORS_TAKE } from "helpers/analyzeConstants"
import { computeNonZerosKeys } from "helpers/analyze"
import { getModelCategories } from "selectors/modelMetadata"

const getCategory = state => get(state, "modules.securityProfileRisk.displayType")

export function computeData(items, risk) {
  return chain(risk)
    .map((datum, index) => {
      const { date } = datum

      const result = chain(items)
        .map(item => ([
          item.id,
          item.fn
            ? item.fn(datum)
            : get(datum, item.field, 0),
        ]))
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

export const getData = createSelector(
  [getAllItems, getFilteredRisk],
  computeData,
)

const getNonZerosKeys = createSelector(
  [getData],
  (data) => computeNonZerosKeys(data, {}, (value) => value !== 0),
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys],
  (data, keys) => data.map(item => pickBy(item, (item, key) => keys[key])),
)

export const computeCategoryItems = (items, keys) => filter(items, item => keys[item.id])

// Security profile attribution panel needs all factors
// Analyze and security profile graphs only want subset (based on number of colors)
export const computeCategoryItemsSubset = (items, keys) => chain(items)
  .thru(items => computeCategoryItems(items, keys))
  .take(ANALYZE_FACTORS_TAKE + 1) // 1 category total item + X items)
  .map((item, index) => ({
    ...item,
    color: item.color || COLORS[index - 1], // Ignore to the total item color
  }))
  .value()

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getCategoryItems = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItems,
)

export const getSingleItemCategories = createSelector(
  [getAllItems, getNonZerosKeys, getModelCategories],
  (items, keys, categories) => chain(items)
    .filter(item => keys[item.id])
    .groupBy(item => item.category)
    .pickBy(category => category.length === 2 && !find(categories, c => c.id === category[0].category).singleFactor)
    .mapValues(category => category[1].label)
    .value(),
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  (items, keys) =>
    chain(items)
      .filter(item => keys[item.id])
      .map(item => item.category)
      .filter(item => !!item)
      .countBy(item => item)
      .omitBy(count => count <= 2)
      .value(),
)

export const getRiskAttribution = createSelector(
  [getRiskAttributionData, getCategory, getAllItems],
  (attribution, category, items) => {
    let attributionCategoryLabel

    if (category === "total" || category === "factors") {
      attributionCategoryLabel = "Top Factor"
    } else {
      attributionCategoryLabel = `Top ${get(find(items, { id: category }), "label")} Factor`
    }

    return {
      category: `${attributionCategoryLabel} Risk Contributors`,
      ...attribution,
    }
  },
)
